import type {
  FulfillmentCarrierVoidOutcome,
  FulfillmentCarrierVoidRecord,
  FulfillmentCarrierVoidState,
  FulfillmentOutboundWaybill,
  MarketingCarrierVoidRetry,
} from '@/infrastructure/FulfillmentAdminClient';

/** The 409 Create Carrier Order answers over a waybill FF2 did not book (SSD-7586); nothing was booked. */
export const EXTERNAL_WAYBILL_RECORDED = 'EXTERNAL_WAYBILL_RECORDED';

/** What became of the carrier booking, in the desk's words. */
export const CARRIER_VOID_STATE_LABEL: Record<FulfillmentCarrierVoidState, string> = {
  VOIDED: 'Cancelled at the carrier',
  NOTHING_BOOKED: 'Nothing to cancel',
  FAILED: 'Carrier cancel failed',
  NOT_ATTEMPTED: 'Not run yet',
};

/**
 * The status chip each state wears: green when the carrier confirmed, blue when there was nothing to cancel, red when it
 * failed, amber while it is still owed.
 */
export const CARRIER_VOID_STATE_CHIP: Record<FulfillmentCarrierVoidState, string> = {
  VOIDED: 'st-processing',
  NOTHING_BOOKED: 'st-completed',
  FAILED: 'st-cancelled',
  NOT_ATTEMPTED: 'st-hold',
};

/**
 * Whether "Retry carrier cancel" can help: a cancel the close still owes, or one that failed for a reason a second try can
 * clear. A cancel the carrier refused for good, or one already settled, offers nothing to press.
 */
export const carrierVoidRetryable = (record: Pick<FulfillmentCarrierVoidRecord, 'state' | 'retryable'>): boolean =>
  record.state === 'NOT_ATTEMPTED' || (record.state === 'FAILED' && record.retryable);

/** "YUN_EXPRESS YT2412345678901234": the parcel a carrier cancel was about, or what is known of it. */
export const carrierParcelOf = (outcome: Pick<FulfillmentCarrierVoidOutcome, 'carrier' | 'trackingNumber'>): string =>
  [outcome.carrier, outcome.trackingNumber].filter(Boolean).join(' ') || 'the parcel';

/**
 * "YUN_EXPRESS YT2412345678901234", or "HAND-1 (carrier unknown)" for a waybill typed in without a carrier (SSD-7586).
 */
export const externalWaybillParcel = (
  waybill: Pick<FulfillmentOutboundWaybill, 'carrier' | 'trackingNumber'>,
): string =>
  waybill.carrier ? `${waybill.carrier} ${waybill.trackingNumber}` : `${waybill.trackingNumber} (carrier unknown)`;

/**
 * Why Create Carrier Order is shut on an order whose outbound waybill FF2 did not book (SSD-7586): booking another
 * parcel would leave two waybills for one box, so the desk clears the outside one first if it really means to. The
 * server refuses Create, Reset and Switch over such a waybill with 409 EXTERNAL_WAYBILL_RECORDED, which reads the same.
 */
export const externalWaybillLine = (waybill: Pick<FulfillmentOutboundWaybill, 'carrier' | 'trackingNumber'>): string =>
  `Booked outside OMS: ${externalWaybillParcel(waybill)}. Clear it first if you really want FF2 to book another parcel.`;

/** The toast a Retry carrier cancel ends with, read off the server's answer for this order. */
export const describeCarrierVoidRetry = (
  answer: MarketingCarrierVoidRetry | undefined,
): { tone: 'success' | 'warning' | 'error'; text: string } => {
  if (!answer) {
    return {
      tone: 'warning',
      text: 'The server answered nothing for this order. Reload to see where the carrier cancel stands.',
    };
  }
  const outcome = answer.outcome;
  switch (answer.result) {
    case 'PENDING':
      return {
        tone: 'warning',
        text: 'The carrier cancel is still running. Its result shows here once it is recorded; reload in a minute.',
      };
    case 'REFUSED':
      return { tone: 'error', text: `Not retried: ${answer.message ?? answer.code ?? 'the server refused it.'}` };
    case 'SETTLED':
      return {
        tone: 'success',
        text:
          outcome?.state === 'NOTHING_BOOKED'
            ? 'Already settled: nothing was booked at the carrier.'
            : `Already settled: ${outcome ? carrierParcelOf(outcome) : 'the parcel'} is cancelled at the carrier.`,
      };
    case 'RETRIED':
    default:
      if (!outcome) {
        return { tone: 'warning', text: 'The carrier cancel ran again. Reload to see how it ended.' };
      }
      if (outcome.state === 'VOIDED') {
        return {
          tone: 'success',
          text: `Carrier cancel done: ${carrierParcelOf(outcome)} is cancelled at the carrier.`,
        };
      }
      if (outcome.state === 'NOTHING_BOOKED') {
        return { tone: 'success', text: 'Nothing was booked at the carrier, so there was nothing to cancel.' };
      }
      return {
        tone: 'error',
        text: `Carrier cancel failed again: ${outcome.message ?? outcome.code ?? 'no reason given'}. ${
          outcome.retryable ? 'Retry later.' : 'The carrier will not cancel it: cancel it by hand at the carrier.'
        }`,
      };
  }
};
